import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronsLeftRight } from "lucide-react";
import { FadeIn } from "./Reveal";
import { BEFORE_AFTER } from "../../lib/images";
import { api, API } from "../../lib/api";

function CompareSlider({ item }) {
    const ref = useRef(null);
    const dragging = useRef(false);
    const [pos, setPos] = useState(50);

    const update = useCallback((clientX) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        const p = ((clientX - rect.left) / rect.width) * 100;
        setPos(Math.min(96, Math.max(4, p)));
    }, []);

    const onPointerDown = (e) => {
        dragging.current = true;
        ref.current.setPointerCapture(e.pointerId);
        update(e.clientX);
    };
    const onPointerMove = (e) => {
        if (dragging.current) update(e.clientX);
    };
    const stop = () => {
        dragging.current = false;
    };
    const onKeyDown = (e) => {
        if (e.key === "ArrowLeft") setPos((p) => Math.max(4, p - 5));
        if (e.key === "ArrowRight") setPos((p) => Math.min(96, p + 5));
    };

    return (
        <div>
            <div
                ref={ref}
                data-testid={`ba-slider-${item.id}`}
                className="group relative aspect-[4/3] cursor-ew-resize touch-none select-none overflow-hidden rounded-3xl shadow-sm"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={stop}
                onPointerCancel={stop}
                role="slider"
                tabIndex={0}
                aria-label={`Porównanie przed i po — ${item.title}`}
                aria-valuenow={Math.round(pos)}
                aria-valuemin={0}
                aria-valuemax={100}
                onKeyDown={onKeyDown}
            >
                <img
                    src={item.after}
                    alt={`${item.title} — po remoncie`}
                    className="absolute inset-0 h-full w-full object-cover"
                    draggable={false}
                    loading="lazy"
                />
                <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
                    <img
                        src={item.before}
                        alt={`${item.title} — przed remontem`}
                        className="absolute inset-0 h-full w-full object-cover"
                        draggable={false}
                        loading="lazy"
                    />
                </div>
                <span className="absolute left-4 top-4 rounded-full bg-ink/70 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white backdrop-blur-md">
                    Przed
                </span>
                <span className="absolute right-4 top-4 rounded-full bg-gold px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-ink">
                    Po
                </span>
                <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
                    <div className="absolute inset-y-0 -left-px w-0.5 bg-white shadow-[0_0_12px_rgba(0,0,0,0.45)]" />
                    <div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold p-2.5 text-ink shadow-xl transition-transform duration-300 group-hover:scale-110">
                        <ChevronsLeftRight className="h-4 w-4" />
                    </div>
                </div>
            </div>
            <p className="mt-4 text-center font-display text-sm font-bold tracking-tight text-ink/70">{item.title}</p>
        </div>
    );
}

export default function BeforeAfter() {
    const [pairs, setPairs] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/before-after");
                if (data.length) {
                    setPairs(
                        data.map((p) => ({
                            id: p.id,
                            title: p.title,
                            before: `${API}/gallery/file/${p.before_path}`,
                            after: `${API}/gallery/file/${p.after_path}`,
                        }))
                    );
                }
            } catch { /* fallback — przykładowe metamorfozy */ }
        })();
    }, []);

    const items = pairs.length ? pairs : BEFORE_AFTER;

    return (
        <div className="mt-24" data-testid="before-after-section">
            <FadeIn>
                <div className="mb-10 text-center">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-gold">Metamorfozy</p>
                    <h3 className="font-display text-3xl font-bold tracking-tighter text-ink md:text-4xl">
                        Przeciągnij suwak — przed i po
                    </h3>
                    <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-ink/50">
                        Zobacz, jak zmieniają się wnętrza po naszych remontach.
                    </p>
                </div>
            </FadeIn>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                {items.map((item, i) => (
                    <FadeIn key={item.id} delay={i * 0.1}>
                        <CompareSlider item={item} />
                    </FadeIn>
                ))}
            </div>
        </div>
    );
}
